import React from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const sections = [
  {
    title: "Tailored Salesforce Solutions",
    text: `We provide customized Salesforce implementations designed to match your business processes. From Sales Cloud to Service Cloud, our team ensures your Salesforce platform is configured to maximize efficiency and drive growth.`,
    img: "../../sale2.jpg",
  },
  {
    title: "Seamless Integration",
    text: `Our experts integrate Salesforce with your existing systems and tools, ensuring smooth data flow and eliminating silos. This helps your teams access real-time insights and make informed decisions effortlessly.`,
    img: "../../sale3.jpg",
    reverse: true,
  },
  {
    title: "Automation & Efficiency",
    text: `We leverage Salesforce automation capabilities to streamline workflows, reduce manual tasks, and boost productivity. Our solutions help you deliver faster responses to customers while maintaining high-quality operations.`,
    img: "../../sales.jpg",
  },
  {
    title: "Analytics & Insights",
    text: `With Salesforce’s powerful analytics tools, we help you gain actionable insights into your sales, marketing, and customer service data. Make smarter decisions, track KPIs, and identify growth opportunities with ease.`,
    img: "../../staff5.jpg",
    reverse: true,
  },
];

const ScrollSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    viewport={{ once: false, amount: 0.3 }}
  >
    {children}
  </motion.div>
);

export default function SalesforceTailwind() {
  const handleScroll = () => {
    document.getElementById("salesforce-sections").scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="font-poppins text-gray-900 overflow-x-hidden scroll-smooth relative">
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url('../../sales.jpg')` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      <ScrollSection>
        <section className="relative h-[90vh] md:h-screen flex flex-col items-center justify-center text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-[64px] font-extrabold uppercase tracking-wider mb-4 text-white drop-shadow-lg"
          >
            Salesforce
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-base sm:text-lg md:text-xl max-w-xl md:max-w-2xl text-gray-300 leading-relaxed px-2"
          >
            Why we are the best Salesforce Implementation Company
          </motion.p>

          <motion.div
            className="absolute bottom-10 sm:bottom-16 cursor-pointer animate-bounce text-red-600 left-1/2 -translate-x-1/2"
            onClick={handleScroll}
            whileHover={{ scale: 1.2 }}
          >
            <ChevronDown size={40} className="sm:w-11 sm:h-11" />
          </motion.div>
        </section>
      </ScrollSection>

      <div id="salesforce-sections" className="max-w-6xl mx-auto py-16 sm:py-20 lg:py-24 px-4 sm:px-6 md:px-10 space-y-20 relative z-10">
        {sections.map(({ title, text, img, reverse }, idx) => (
          <ScrollSection key={idx}>
            <div
              className={`flex flex-col md:flex-row items-center gap-10 md:gap-12 ${reverse ? "md:flex-row-reverse" : ""}`}
            >
              <motion.div
                className="flex-1 bg-white/95 backdrop-blur-lg p-6 sm:p-8 md:p-10 rounded-2xl shadow-[0_10px_30px_rgba(209,17,17,0.15)] hover:shadow-[0_14px_40px_rgba(209,17,17,0.3)] transition-all duration-300"
                whileHover={{ y: -5 }}
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 relative after:block after:w-20 after:h-1 after:bg-red-600 after:rounded mt-2">
                  {title}
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-gray-700">{text}</p>
              </motion.div>

              <motion.div
                className="flex-1 text-center"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                <img
                  src={img}
                  alt={title}
                  className="w-full max-w-sm sm:max-w-md md:max-w-lg rounded-2xl shadow-[0_12px_30px_rgba(209,17,17,0.2)]"
                />
              </motion.div>
            </div>
          </ScrollSection>
        ))}
      </div>

      <style>{`
        @keyframes bounce {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        .animate-bounce {
          animation: bounce 1.8s infinite;
        }
      `}</style>
    </div>
  );
}